import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';
import { selectRecordsByUiFilterKind } from '@mm/state/selectors/campaignsOverviewSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { PaginationNav } from '../../../../../../common/components/PaginationNav/PaginationNav';
import { CampaignsTable } from '../../Campaigns/CampaignsTable/CampaignsTable';
import { ReactComponent as Art } from './svg/art.svg';
import bgSrc from './svg/bg.svg';

import css from './AutomationIndex.module.css';
import { TemplateGoButton } from './TemplateIndex/TemplateGoButton';
import { AutomationTemplateContainer } from './TemplateIndex/AutomationTemplateContainer';
import { PlusOutlined } from '@ant-design/icons';

interface Props {
  className: string;
}

export const AutomationIndex: FunctionComponent<Props> = props => {
  const { className } = props;
  const botId = useSelector(activeChatbotIdSelector);

  const [busy, setBusy] = useState(false);

  const { campaigns: automations, pagination } =
    useSelector((state: StoreState) =>
      selectRecordsByUiFilterKind(state, 'automation'),
    ) || {};

  const campaignsOverviewCRUDActions = useCampaignsOverviewCRUDActions();

  const fetchPage = useCallback(
    async (page?: number) => {
      setBusy(true);
      try {
        await campaignsOverviewCRUDActions.retrieveAll({
          botId,
          kind: 'automation',
          pagination: {
            page: page || pagination?.page || 1,
            perPage: pagination?.perPage || 8,
          },
        });
      } finally {
        setBusy(false);
      }
    },
    [botId, campaignsOverviewCRUDActions],
  );

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  if (!automations) {
    return null;
  }
  return (
    <div className={className}>
      {!automations?.length && !busy && (
        <div className={css.empty} style={{ backgroundImage: `url(${bgSrc})` }}>
          <Art />
          <span className={css.emptyTitle}>
            This is where you'll see your exciting automations that find your
            unicorns in a sea of donkeys!
          </span>

          <AutomationTemplateContainer.Provider>
            <TemplateGoButton
              label="Create one now!"
              leftIcon={<PlusOutlined />}
              css={{ button: css.addAutomationButton }}
            />
          </AutomationTemplateContainer.Provider>
        </div>
      )}

      {automations?.length && !busy && (
        <>
          <CampaignsTable
            campaigns={automations}
            fetchCurrentActiveKind={fetchPage}
          />
          {pagination && (
            <PaginationNav
              className={css.pagination}
              pagination={pagination}
              onChangePage={fetchPage}
              rangeLength={5}
              withEllipsis
              circularActive
            />
          )}
        </>
      )}
    </div>
  );
};
